import { useEffect, useRef, lazy, Suspense } from "react";
import { ClerkProvider, SignIn, SignUp, useAuth, useClerk } from '@clerk/react';
import { publishableKeyFromHost } from '@clerk/react/internal';
import { shadcn } from '@clerk/themes';
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from 'wouter';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache, useQueryClient } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { captureError } from "@workspace/observability/web";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import NotFound from "@/pages/not-found";
import PlayerEntry from "@/player/player-entry";
import { isPlayerSurface } from "@/lib/player-surface";
import { isDesktopShell } from "@/lib/platform";
import { hasOperatorExited } from "@/lib/operator-exit";
import { sanitizeRedirectPath } from "@/lib/redirect-sanitizer";
import { PersistentPlayerRoot } from "@/components/player/persistent-player-root";
import { AuthLoadingScreen, ProtectedPage, RequireAuth } from "@/route-guards";
import { MARKETING_ROUTE_COMPONENTS } from "@/marketing/routes";
import { isAppRoutePath } from "@/lib/app-routes";

// Operator surfaces — always present (web build AND slim desktop build). Code-
// split so marketing visitors never download the app.
const ModeSelectPage = lazy(() => import("@/pages/mode-select"));
const EntryScreen = lazy(() => import("@/pages/entry"));
const WorkspacesPage = lazy(() => import("@/pages/workspaces"));
const PlayerSelectionPage = lazy(() => import("@/pages/player-selection"));
const PlayerCompanionPage = lazy(() => import("@/pages/player-companion"));
const ControlSelectionPage = lazy(() => import("@/pages/control-selection"));
const ControlPage = lazy(() => import("@/pages/control"));
// Desktop first-run intro — registered on both builds (tiny page), but only
// reachable inside the Electron shell; the web build redirects through it.
const DesktopIntroPage = lazy(() => import("@/pages/desktop-intro"));

// `VITE_APP_SURFACE=desktop` builds the slim, operator-only desktop app. The
// admin, marketing and legal surfaces below are referenced ONLY when NOT
// desktop, so Rollup dead-code-eliminates them (and the ~18MB marketing hero
// video) out of that bundle.
const IS_DESKTOP = import.meta.env.VITE_APP_SURFACE === "desktop";
const AdminRoutes = IS_DESKTOP ? null : lazy(() => import("@/admin-routes"));
// Master kill switches (default on) — VITE_AD_MARKETPLACE_ENABLED=false /
// VITE_DEVICE_HARDWARE_ENABLED=false remove these routes from the client
// bundle's reachable surface entirely, not just hide a nav link. Pair with
// the server-side AD_MARKETPLACE_ENABLED / DEVICE_HARDWARE_ENABLED.
const IS_AD_MARKETPLACE_ENABLED =
  import.meta.env.VITE_AD_MARKETPLACE_ENABLED !== "false";
const IS_DEVICE_HARDWARE_ENABLED =
  import.meta.env.VITE_DEVICE_HARDWARE_ENABLED !== "false";
// The Ad Marketplace portal is a web-only advertiser surface; the slim desktop
// build never navigates there (HomeRedirect sends desktop straight to
// /workspaces), so gate it to null for tree-shaking.
const MarketplacePortal =
  IS_DESKTOP || !IS_AD_MARKETPLACE_ENABLED
    ? null
    : lazy(() => import("@/pages/marketplace"));
const AdminMarketplace =
  IS_DESKTOP || !IS_AD_MARKETPLACE_ENABLED
    ? null
    : lazy(() => import("@/pages/admin-marketplace"));
const AdminInventory =
  IS_DESKTOP || !IS_DEVICE_HARDWARE_ENABLED
    ? null
    : lazy(() => import("@/pages/admin-inventory"));
const AdminOrders =
  IS_DESKTOP || !IS_DEVICE_HARDWARE_ENABLED
    ? null
    : lazy(() => import("@/pages/admin-orders"));
const AdminCustomers = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-customers"));
// Platform-operator console: index/overview + cross-tenant fleet, prospects
// CRM and audit trail. All server-gated by requirePlatformAdmin.
const AdminIndex = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-index"));
const AdminFleet = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-fleet"));
const AdminSupport = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-support"));
const AdminProspects = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-prospects"));
const AdminAudit = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-audit"));
const AdminGrowth = IS_DESKTOP ? null : lazy(() => import("@/pages/admin-growth"));
const Landing = IS_DESKTOP ? null : lazy(() => import("@/pages/landing"));
// Secondary marketing pages (/features, /apps, /compare). Table lives in
// @/marketing/routes so route↔metadata
// parity is testable (marketing/routes.test.ts) and the prerenderer + sitemap
// stay in step. Tree-shaken out of the slim desktop build with the rest of
// the marketing surface.
const MARKETING_ROUTES = IS_DESKTOP ? {} : MARKETING_ROUTE_COMPONENTS;
// Public 404 for URLs that are neither a marketing route nor an app route
// (AppOrNotFound below). Absent from the slim desktop build like the rest of
// the marketing surface.
const MarketingNotFound = IS_DESKTOP
  ? null
  : lazy(() => import("@/pages/marketing/not-found"));
const Privacy = IS_DESKTOP ? null : lazy(() => import("@/pages/privacy"));
const Terms = IS_DESKTOP ? null : lazy(() => import("@/pages/terms"));
const Cookies = IS_DESKTOP ? null : lazy(() => import("@/pages/cookies"));
// Public (unauthenticated) account-deletion instructions. This is the URL in
// the Play Data-safety form and App Store Connect, so it must never move
// behind the auth guard.
const DeleteAccount = IS_DESKTOP
  ? null
  : lazy(() => import("@/pages/delete-account"));
const InviteAcceptPage = IS_DESKTOP ? null : lazy(() => import("@/pages/invite"));
// Physical-device "thank you for your purchase" page — reached by scanning a
// QR code on a device/box label. Same gate as the admin device-inventory /
// device-orders pages: hardware-sales-only surface, tree-shaken with them
// when device hardware sales are disabled.
const WelcomePage =
  IS_DESKTOP || !IS_DEVICE_HARDWARE_ENABLED
    ? null
    : lazy(() => import("@/pages/welcome"));

import { readStoredCredential } from "@/player/lib/screen-credential";
import { shouldShowDesktopIntro } from "@/lib/desktop-intro";
import {
  ACTIVE_WORKSPACE_STORAGE_KEY,
  WorkspaceProvider,
} from "@/hooks/use-workspace";
import { RealtimeProvider } from "@/hooks/realtime-context";
import { WorkspaceAccentController } from "@/components/branding/workspace-accent-controller";
import { BrandingSessionSentinel } from "@/components/branding/branding-session-sentinel";
import { clearPersistedBranding } from "@/lib/workspace-branding-storage";
import { clearWorkspaceAccent } from "@/lib/workspace-branding";
import { useGetSession, getGetSessionQueryOptions } from "@workspace/api-client-react";
import { clerkAuthLocalization } from "@/lib/auth-copy";
import { ReferralClaimer, referralForSignUp } from "@/components/referral-claimer";
import {
  isLocalDevHost,
  remoteApiOrigin,
  usesRemoteApiOnLocalhost,
} from "@/lib/api-config";
import { useRemoteApiAuthToken } from "@/hooks/use-remote-api-auth-token";
import { CookieConsentBanner } from "@/components/consent/cookie-banner";
import {
  ObservabilityUserSync,
  PageViewTracker,
} from "@/components/observability/observability-bridge";

const queryClient = new QueryClient({
  // Without defaultOptions React Query v5 applies staleTime: 0 to all ~141
  // useGet*/useList* call sites in this app, of which only 13 set it locally.
  // Everything was therefore considered stale the instant it arrived, so every
  // tab focus, every remount and every navigation back re-fired every mounted
  // query at full price -- including the ~175-210ms Clerk auth tax each request
  // pays. The TV app already sets sane defaults; the console, which queries far
  // more, had none.
  defaultOptions: {
    queries: {
      // Long enough to collapse a focus/remount/navigate burst into one fetch,
      // short enough that a returning operator still sees fresh state. Live
      // changes arrive over the realtime socket regardless, and any mutation
      // that calls invalidateQueries still refetches immediately -- invalidation
      // marks data stale explicitly and ignores staleTime.
      staleTime: 30_000,
      // The default of 3 turns one dead endpoint into four requests plus
      // exponential backoff, which is the wrong trade for an operator console
      // that would rather surface the error quickly.
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      captureError(error, { source: "react-query" });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      captureError(error, { source: "react-query-mutation" });
    },
  }),
});

const configuredClerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const configuredClerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

function RemoteApiAuthBridge({ children }: { children: React.ReactNode }) {
  const { authReady } = useRemoteApiAuthToken();
  if (usesRemoteApiOnLocalhost() && !authReady) {
    return <AuthLoadingScreen />;
  }
  return <>{children}</>;
}

function resolveClerkPublishableKey(): string {
  const hostname = window.location.hostname;

  // publishableKeyFromHost("localhost") synthesizes clerk.localhost, which only
  // exists behind the dev proxy — always use the real dev key locally.
  if (isLocalDevHost(hostname)) {
    if (!configuredClerkPubKey) {
      throw new Error(
        "Missing VITE_CLERK_PUBLISHABLE_KEY. Add it to artifacts/web/.env for local development.",
      );
    }
    return configuredClerkPubKey;
  }

  // A development Clerk instance (pk_test_) is origin-agnostic: its Frontend API
  // lives at <slug>.clerk.accounts.dev and accepts any host. Deriving a key from
  // the serving hostname (below) only makes sense for a pk_live_ instance on a
  // custom domain (e.g. clerk.omnimedia.app). Using the configured test key
  // directly lets non-apex deployments (staging, preview envs) authenticate
  // instead of pointing Clerk at a clerk.<host> Frontend API that doesn't exist.
  if (configuredClerkPubKey?.startsWith("pk_test_")) {
    return configuredClerkPubKey;
  }

  return (
    publishableKeyFromHost(hostname, configuredClerkPubKey) ??
    configuredClerkPubKey ??
    ""
  );
}

// Must match CLERK_PROXY_PATH in
// artifacts/api-server/src/middlewares/clerkProxyMiddleware.ts.
const CLERK_PROXY_PATH = "/api/__clerk";

function resolveClerkProxyUrl(): string | undefined {
  // A DEVELOPMENT instance (pk_test_) has no proxy at all: its Frontend API
  // lives at <slug>.clerk.accounts.dev and accepts ANY origin, loopback
  // included — which is exactly why resolveClerkPublishableKey above returns
  // such a key verbatim instead of deriving one from the host. Pointing it at
  // a proxy makes the handshake fail (host_invalid / a 400 on
  // /api/__clerk/v1/dev_browser), ClerkProvider never loads, and the window
  // hangs on its boot logo forever. CB-163 fixed that for the desktop HUB via
  // the same-origin guard below, but the hub loads the SERVER's web build
  // (VITE_APP_SURFACE is unset there, so IS_DESKTOP is false) while the player
  // window loads the locally bundled desktop build and took the IS_DESKTOP
  // branch first — so against a dev server the player window still hung.
  // Both proxy branches are covered here, ahead of either.
  if (configuredClerkPubKey?.startsWith("pk_test_")) return undefined;
  // The desktop shell serves its PLAYER from a loopback origin (http://127.0.0.1)
  // but authenticates against the remote server. Clerk MUST be routed through
  // that server's proxy (`/api/__clerk`) — a production Clerk instance won't
  // CORS-allow a bare loopback origin talking to the FAPI directly. Derive the
  // proxy from the server URL the shell injects, which also makes a self-hosted
  // server work. This runs BEFORE the isLocalDevHost bail-out below, which
  // exists for the plain web dev server and must NOT catch the desktop loopback.
  if (IS_DESKTOP) {
    const serverUrl = window.omnimediaDesktop?.serverUrl
      ?.trim()
      .replace(/\/+$/, "");
    if (serverUrl) return `${serverUrl}/api/__clerk`;
  }
  // A page talking to a REMOTE api-server it is NOT served from (the loopback
  // player window, or a dev Mac using VITE_API_URL against the droplet): that
  // server issues Clerk session tokens with its own proxy path as the issuer,
  // so Clerk must be pointed at that same proxy.
  //
  // CRITICAL same-origin guard (CB-163): the desktop HUB loads this full web
  // build FROM the remote origin itself — first-party, exactly like the
  // website — and the preload bridge exists there too, so remoteApiOrigin() is
  // non-null. Routing THAT page through the proxy broke the hub outright
  // against dev (a development Clerk instance has no proxy → host_invalid →
  // ClerkProvider never loads → the app hangs on the boot spinner). First-party
  // pages always use the direct FAPI, like any browser tab.
  const remote = remoteApiOrigin();
  if (remote && window.location.origin !== remote) {
    return `${remote}${CLERK_PROXY_PATH}`;
  }

  const proxyUrl = configuredClerkProxyUrl?.trim();
  if (!proxyUrl || isLocalDevHost(window.location.hostname)) {
    return undefined;
  }
  return proxyUrl;
}

const clerkPubKey = resolveClerkPublishableKey();
const clerkProxyUrl = resolveClerkProxyUrl();
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.svg`,
  },
  variables: {
    colorPrimary: "hsl(250 85% 50%)",
    colorForeground: "hsl(240 10% 10%)",
    colorMutedForeground: "hsl(240 3.8% 46.1%)",
    colorDanger: "hsl(0 84.2% 60.2%)",
    colorBackground: "hsl(0 0% 100%)",
    colorInput: "hsl(240 5.9% 90%)",
    colorInputForeground: "hsl(240 10% 10%)",
    colorNeutral: "hsl(240 5.9% 90%)",
    fontFamily: "'Space Grotesk', 'Inter', sans-serif",
    borderRadius: "0.75rem",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "bg-white dark:bg-zinc-950 rounded-2xl w-[440px] max-w-full overflow-hidden",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-foreground",
    headerSubtitle: "text-muted-foreground",
    socialButtonsBlockButtonText: "text-foreground",
    formFieldLabel: "text-foreground",
    footerActionLink: "text-primary hover:text-primary/90",
    footerActionText: "text-muted-foreground",
    dividerText: "text-muted-foreground",
    identityPreviewEditButton: "text-primary hover:text-primary/90",
    formFieldSuccessText: "text-green-600",
    alertText: "text-destructive",
    logoBox: "",
    logoImage: "",
    socialButtonsBlockButton: "border-border bg-background hover:bg-muted",
    formButtonPrimary: "bg-primary text-primary-foreground hover:bg-primary/90",
    formFieldInput: "bg-background border-border text-foreground focus:ring-primary",
    footerAction: "",
    dividerLine: "bg-border",
    alert: "border-destructive text-destructive",
    otpCodeFieldInput: "border-border text-foreground focus:ring-primary",
    formFieldRow: "",
    main: "",
  },
};

function signInRedirectUrl(): string {
  const params = new URLSearchParams(window.location.search);
  // Only internal single-slash paths survive; anything origin-escaping
  // ("//evil.com", "https://evil.com") falls back to "/", which resolves via
  // HomeRedirect → /home on web, /workspaces on desktop.
  return `${basePath}${sanitizeRedirectPath(params.get("redirect_url"))}`;
}

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        forceRedirectUrl={signInRedirectUrl()}
      />
    </div>
  );
}

function SignUpPage() {
  // Partner attribution rides through Clerk (survives OAuth redirects and
  // email verification); ReferralClaimer posts it once the user exists.
  const referral = referralForSignUp();
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
        forceRedirectUrl={`${basePath}/`}
        unsafeMetadata={referral ? { referral } : undefined}
      />
    </div>
  );
}

function HomeRedirect() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoading: sessionLoading } = useGetSession({
    query: {
      ...getGetSessionQueryOptions(),
      enabled: isLoaded && isSignedIn,
    },
  });

  if (readStoredCredential()) {
    if (isPlayerSurface()) {
      return <Redirect to="/player" />;
    }
    if (!isLoaded) {
      return <AuthLoadingScreen />;
    }
    if (!isSignedIn) {
      // A deliberate operator sign-out this session goes to /sign-in — only a
      // genuine kiosk boot (no operator exit recorded) auto-resumes playback.
      return <Redirect to={hasOperatorExited() ? "/sign-in" : "/player"} />;
    }
    return <Redirect to="/workspaces" />;
  }

  if (!isLoaded || (isSignedIn && sessionLoading)) {
    return <AuthLoadingScreen />;
  }

  if (isSignedIn) {
    // Web lands on the two-choice entry screen (System vs Ad Marketplace). The
    // desktop shell — which now loads this same full web build from the remote
    // origin, so the IS_DESKTOP *build* flag is false here — has no use for the
    // marketplace entry screen, so it goes straight to workspaces. Keyed on the
    // runtime bridge, not the build flag, precisely because the hub is no longer
    // the slim build.
    return <Redirect to={isDesktopShell() ? "/workspaces" : "/home"} />;
  }

  // The slim desktop build has no marketing landing — go straight to sign-in.
  return Landing ? <Landing /> : <Redirect to="/sign-in" />;
}

/** Signed-in app shell: one workspace + realtime connection for all control routes. */
function AuthenticatedApp() {
  // Desktop first-run: the Electron main window opens /start, which sits
  // behind RequireAuth — front the one-time intro here so a fresh install
  // meets the product before it meets a login form (lib/desktop-intro.ts).
  if (shouldShowDesktopIntro()) {
    return <Redirect to="/desktop-intro" />;
  }
  return (
    <RequireAuth>
      <WorkspaceProvider>
        <ObservabilityUserSync />
        <WorkspaceAccentController />
        <RealtimeProvider>
          <div className="h-full min-h-0">
          <Suspense fallback={<AuthLoadingScreen />}>
          <Switch>
            <Route path="/start">
              <ProtectedPage component={ModeSelectPage} />
            </Route>
            <Route path="/home">
              <ProtectedPage component={EntryScreen} requireWorkspace={false} />
            </Route>
            <Route path="/workspaces">
              <ProtectedPage component={WorkspacesPage} requireWorkspace={false} />
            </Route>
            {/* Standalone advertiser portal — web-only, reachable by any
                signed-in user; advertiser/workspace resolution happens inside
                the portal (MarketplaceGate), not in the router. Matches
                `/marketplace` and any subpath Phase 3 adds. */}
            {MarketplacePortal && (
              <Route path="/marketplace/*?">
                <ProtectedPage
                  component={MarketplacePortal}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {/* Platform-operator marketplace oversight (server-gated by
                requirePlatformAdmin; non-operators get a 403 error state). */}
            {AdminMarketplace && (
              <Route path="/admin/marketplace">
                <ProtectedPage
                  component={AdminMarketplace}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {/* Platform-operator device inventory + label printing (server-gated
                by requirePlatformAdmin; non-operators get a 403 error state). */}
            {AdminInventory && (
              <Route path="/admin/inventory">
                <ProtectedPage
                  component={AdminInventory}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {AdminOrders && (
              <Route path="/admin/orders">
                <ProtectedPage
                  component={AdminOrders}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {/* Platform-operator "Pilot Ops" dashboard (server-gated by
                requirePlatformAdmin; non-operators get a 403 error state). */}
            {AdminCustomers && (
              <Route path="/admin/customers">
                <ProtectedPage
                  component={AdminCustomers}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {/* Platform-operator console index + cross-tenant pages (server-
                gated by requirePlatformAdmin; non-operators get a 403 error
                state). */}
            {AdminGrowth && (
              <Route path="/admin/growth">
                <ProtectedPage
                  component={AdminGrowth}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {AdminIndex && (
              <Route path="/admin">
                <ProtectedPage component={AdminIndex} requireWorkspace={false} />
              </Route>
            )}
            {AdminFleet && (
              <Route path="/admin/fleet">
                <ProtectedPage component={AdminFleet} requireWorkspace={false} />
              </Route>
            )}
            {AdminSupport && (
              <Route path="/admin/support">
                <ProtectedPage
                  component={AdminSupport}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {AdminProspects && (
              <Route path="/admin/prospects">
                <ProtectedPage
                  component={AdminProspects}
                  requireWorkspace={false}
                />
              </Route>
            )}
            {AdminAudit && (
              <Route path="/admin/audit">
                <ProtectedPage component={AdminAudit} requireWorkspace={false} />
              </Route>
            )}
            <Route path="/player-selection">
              <ProtectedPage component={PlayerSelectionPage} requireWorkspace={false} />
            </Route>
            <Route path="/player-companion">
              <ProtectedPage component={PlayerCompanionPage} requireWorkspace={false} />
            </Route>
            <Route path="/control-selection">
              <ProtectedPage component={ControlSelectionPage} requireWorkspace={false} />
            </Route>
            <Route path="/control/:deviceId">
              <ProtectedPage component={ControlPage} />
            </Route>
            {/* Admin/config surface — only on the full web build; the slim
                desktop build falls through to NotFound. */}
            <Route>{AdminRoutes ? <AdminRoutes /> : <NotFound />}</Route>
          </Switch>
          </Suspense>
          </div>
        </RealtimeProvider>
      </WorkspaceProvider>
    </RequireAuth>
  );
}

/**
 * The router's fallback. Every public route is matched above, so what lands
 * here is either one of the signed-in app's own routes (lib/app-routes.ts),
 * which AuthenticatedApp guards and renders, or an unknown public URL, which
 * gets the marketing 404 instead of a bounce to the sign-in form. One stable
 * fallback element keeps AuthenticatedApp (workspace + realtime providers)
 * mounted across app navigations exactly as before. The slim desktop build has
 * no marketing surface and keeps falling into the app shell.
 */
function AppOrNotFound() {
  const [location] = useLocation();
  if (MarketingNotFound && !isAppRoutePath(location)) {
    return <MarketingNotFound />;
  }
  return <AuthenticatedApp />;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      // Only clear when a real signed-in user changes or signs out. Clerk's
      // first callback in a fresh window commonly reports null while the
      // session is still restoring; treating that null → userId transition as
      // a user switch wiped the cache and active workspace on every new
      // window (e.g. the desktop player window).
      if (prevUserIdRef.current != null && prevUserIdRef.current !== userId) {
        queryClient.clear();
        localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY);
        // The persisted accent is keyed on that workspace id, so dropping it
        // here keeps the two from ever disagreeing.
        clearPersistedBranding();
        clearWorkspaceAccent();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      signInFallbackRedirectUrl={`${basePath}/`}
      signUpFallbackRedirectUrl={`${basePath}/`}
      afterSignOutUrl={`${basePath}/sign-in`}
      localization={clerkAuthLocalization}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* Global motion policy (M9.0): every shared variant collapses to
              opacity-only / instant when the viewer prefers reduced motion,
              so no component needs its own guard. */}
          <MotionConfig reducedMotion="user">
          <RemoteApiAuthBridge>
          <ClerkQueryClientCacheInvalidator />
          <BrandingSessionSentinel />
          <PageViewTracker />
          <ReferralClaimer />
          <PersistentPlayerRoot>
            <div className="h-full">
            <Suspense fallback={<AuthLoadingScreen />}>
            <Switch>
              <Route path="/" component={HomeRedirect} />
              <Route path="/sign-in/*?" component={SignInPage} />
              <Route path="/sign-up/*?" component={SignUpPage} />
              <Route path="/player" component={PlayerEntry} />
              {/* Desktop-only first-run intro; sits outside RequireAuth so it
                  renders before any sign-in form. */}
              <Route path="/desktop-intro" component={DesktopIntroPage} />
              {Object.entries(MARKETING_ROUTES).map(([path, Component]) => (
                <Route key={path} path={path} component={Component} />
              ))}
              {Privacy && <Route path="/privacy" component={Privacy} />}
              {Terms && <Route path="/terms" component={Terms} />}
              {Cookies && <Route path="/cookies" component={Cookies} />}
              {DeleteAccount && (
                <Route path="/delete-account" component={DeleteAccount} />
              )}
              {InviteAcceptPage && (
                <Route path="/invite/:token" component={InviteAcceptPage} />
              )}
              {WelcomePage && <Route path="/welcome" component={WelcomePage} />}
              <Route component={AppOrNotFound} />
            </Switch>
            </Suspense>
            </div>
          </PersistentPlayerRoot>
          <CookieConsentBanner />
          <Toaster />
          </RemoteApiAuthBridge>
          </MotionConfig>
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
